export interface Stat {
  value: string
  label: string
}

export interface Metric {
  value: string
  label: string
}

export interface Volume {
  number: string
  title: string
  pages: string
  body: string
  tags: string[]
}

export interface Bonus {
  number: string
  title: string
  value: string
  body: string
  cover: string
}

export interface Quote {
  initials: string
  name: string
  role: string
  quote: string
  photo?: string
}

export interface Faq {
  question: string
  answer: string
}

export interface ComparisonRow {
  without: string
  with: string
}

export const images = {
  heroMockup:
    'https://cdn.magicpatterns.com/uploads/txwgxbG3Uqt5zdxe7KRD8B/ChatGPT_Image_10_sept_2026,_09_44_12_p.m..png',
  sheetsFlatlay:
    'https://cdn.magicpatterns.com/patterns/generated-images/05603c8a-502a-4a27-ab59-0a7108953045.jpg',
  tracksVisual:
    'https://cdn.magicpatterns.com/patterns/generated-images/d62c7a84-69e3-4354-b883-5f80f004b038.jpg',
}

export const heroBadges = [
  '6 complete guitar volumes',
  '6 exclusive guitar bonuses',
  '300+ ready-to-print pages',
  '150+ exercises, tabs & worksheets',
  '100% PDF — sent to your email',
  'From first chord to CAGED',
]

export const heroStats: Stat[] = [
  { value: '300+', label: 'Ready-to-print guitar pages' },
  { value: '6 + 6', label: 'Volumes and bonuses included' },
  { value: '50+', label: 'Structured guitar lesson plans' },
  { value: 'Kids to adults', label: 'First chord through CAGED' },
]

export const heroQuotes: Quote[] = [
  {
    initials: 'SJ',
    name: 'Sarah J.',
    role: 'Guitar Teacher',
    quote:
      'Before TeachersKit, I was spending 5 hours a week just looking for guitar material. Now, I print and go.',
  },
  {
    initials: 'DM',
    name: 'Daniel M.',
    role: 'Guitar Teacher',
    quote:
      'My beginners are strumming real songs by week three. The chord and strumming sheets alone were worth the price.',
  },
  {
    initials: 'LC',
    name: 'Laura C.',
    role: 'Guitar School Director',
    quote:
      'Every guitar teacher in my school now works from the same plan. Parents can finally see the progress.',
  },
]

export const pains: string[] = [
  'You spend hours hunting YouTube and tab sites for guitar material that looks professional, only to find junk',
  'You hand out the same chord sheets again because you’ve run out of fresh material',
  'You don’t have a logical progression from first chord to soloing mapped out for the year',
  'You’re losing students after month two because they get bored with old, dusty guitar method books',
  'You feel unprepared and stressed before every single lesson',
  'You’re a great guitarist, but you’re exhausted from improvising every class',
]

export const solutionMetrics: Metric[] = [
  { value: '300+', label: 'Practical guitar PDF pages' },
  { value: '150+', label: 'Ready-to-teach exercises' },
  { value: '50+', label: 'Complete guitar lesson plans' },
  { value: '6', label: 'Complete volumes' },
]

export const coverage: { number: string; label: string }[] = [
  { number: '01', label: 'First chords & fretboard basics' },
  { number: '02', label: 'Strumming & rhythm' },
  { number: '03', label: 'Tabs, melodies & songs' },
  { number: '04', label: 'Scales, blues & improvisation' },
  { number: '05', label: 'CAGED & technical development' },
  { number: '06', label: 'Assessment & progress tracking' },
]

export const volumes: Volume[] = [
  {
    number: '01',
    title: 'The Modern Guitar Teacher’s Map',
    pages: '50+ PDF pages',
    body: 'The full route from first lesson to advanced student: how to sequence the year, set objectives and know exactly what comes next.',
    tags: ['Year Plan', 'Student Levels', 'Lesson Structure', 'Objectives'],
  },
  {
    number: '02',
    title: 'Fretboard Architecture & First Chords',
    pages: '50+ PDF pages',
    body: 'Note names, string logic and the first open chords explained so a total beginner understands the fretboard instead of memorising shapes.',
    tags: ['Fretboard Maps', 'Open Chords', 'Finger Placement', 'First Songs'],
  },
  {
    number: '03',
    title: 'Chord Mastery & Strumming',
    pages: '50+ PDF pages',
    body: 'Chord families, clean transitions and strumming patterns by difficulty — the exact drills that stop students getting stuck.',
    tags: ['Chord Families', 'Transitions', 'Strumming Patterns', 'Barre Chords'],
  },
  {
    number: '04',
    title: 'Guitar Tabs, Melodies & Rhythm',
    pages: '50+ PDF pages',
    body: 'Reading tabs, playing melodies and locking in rhythm, with worksheets that turn timing into something students can feel.',
    tags: ['Reading Tabs', 'Melodies', 'Rhythm Drills', 'Timing Games'],
  },
  {
    number: '05',
    title: 'Scales, Blues, Improvisation & Performance',
    pages: '50+ PDF pages',
    body: 'Pentatonic and blues scales, first solos and improvisation frameworks, plus everything students need before playing in front of people.',
    tags: ['Pentatonics', 'Blues', 'Improvisation', 'Performance Prep'],
  },
  {
    number: '06',
    title: 'CAGED, Songs & Technical Development',
    pages: '50+ PDF pages',
    body: 'The CAGED system, repertoire building and advanced technique routines to keep intermediate students progressing for years.',
    tags: ['CAGED System', 'Repertoire', 'Technique Routines', 'Advanced Drills'],
  },
]

export const bonuses: Bonus[] = [
  {
    number: '01',
    title: 'The Guitar Tab Starter Vault',
    value: '$47',
    body: 'A library of beginner-friendly tabs, ready to print and hand out from the very first lesson.',
    cover: 'https://cdn.magicpatterns.com/uploads/iyS6dGKKvTcqfb8pfBmBmt/guitarra_ing.webp',
  },
  {
    number: '02',
    title: 'The Beginner Guitar Melody Builder',
    value: '$47',
    body: 'Simple melodies that get new students playing something recognizable in their first weeks.',
    cover: 'https://cdn.magicpatterns.com/uploads/tvqqb5WExzK2LV2vZ3xEQh/guitarra_ing_2.webp',
  },
  {
    number: '03',
    title: 'The 30-Day Guitar Exercise Calendar',
    value: '$37',
    body: 'A day-by-day home practice plan you can print and send home, so progress never stalls between lessons.',
    cover: 'https://cdn.magicpatterns.com/uploads/qSxsxY8noo14QQF2L1Qqqr/guitarra_ing_3.webp',
  },
  {
    number: '04',
    title: 'The Guitar Technique Drill Pack',
    value: '$57',
    body: 'Focused drills for fingering, picking and chord changes, organized by difficulty level.',
    cover: 'https://cdn.magicpatterns.com/uploads/rfLd6tAmRHR334SzWKQM19/guitarra_ing_4.webp',
  },
  {
    number: '05',
    title: 'The Ready-to-Teach Guitar Lesson Sheets',
    value: '$67',
    body: 'Complete lesson sheets with objectives, exercises and homework — print one and teach it today.',
    cover: 'https://cdn.magicpatterns.com/uploads/rn3w8Zuw7SM2D6Znv1dWPE/guitarra_ing_5.webp',
  },
  {
    number: '06',
    title: '10 Sheet Music Pieces with Ready-to-Use Exercises',
    value: '$47',
    body: 'Ten pieces of sheet music, each paired with its own set of exercises so every piece becomes a full lesson.',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/32f00596-ea02-4de3-b842-9bf9a700e396.jpg',
  },
]

export const packageIncludes: string[] = [
  '6 complete guitar teaching volumes',
  '6 exclusive guitar bonus guides — free',
  '300+ ready-to-print guitar pages',
  '50+ structured guitar lesson plans',
  '100% PDF — no videos, no audiobooks',
  'Delivered to your email right after purchase',
]

export const testimonials: Quote[] = [
  {
    initials: 'SJ',
    name: 'Sarah J.',
    role: 'Guitar Teacher',
    quote:
      'Before TeachersKit, I was spending 5 hours a week just looking for things to teach. Now, I print and go. My students are making faster progress, and I haven’t had a single dropout in 3 months.',
    photo: 'https://cdn.magicpatterns.com/uploads/kNR1sY7Mk6XAp5FZyzoSPg/im_2.jpg',
  },
  {
    initials: 'LC',
    name: 'Laura C.',
    role: 'Guitar School Director',
    quote:
      'Every guitar teacher in my school now works from the same material. The lesson sheets alone made this a no-brainer.',
    photo: 'https://cdn.magicpatterns.com/uploads/d6zHptg9XA5TB8xtMyTyn1/img_3.jpg',
  },
  {
    initials: 'RT',
    name: 'Rachel T.',
    role: 'Private Guitar Studio Owner',
    quote:
      'The assessment sheets changed my parent meetings. I can show concrete progress instead of just saying “she’s doing well.”',
    photo: 'https://cdn.magicpatterns.com/uploads/swkRsUTKCD32aDh46FzXj6/img_4.jpg',
  },
  {
    initials: 'DM',
    name: 'Daniel M.',
    role: 'Guitar Teacher',
    quote:
      'The technique drills and tab vault got my beginners playing real songs by week three. Nothing else I bought did that.',
    photo: 'https://cdn.magicpatterns.com/uploads/bNAPjbVdTefGkpCFEN6t5g/img1_.jpg',
  },
]

export const guaranteeChips = ['Simple refund', 'No forms to fill', 'Keep all bonuses']

export const faqs: Faq[] = [
  {
    question: 'Is this a physical book? What format is it?',
    answer:
      'It’s 100% digital and 100% PDF — there are no videos and no audiobooks. Every volume and bonus is a print-ready PDF you receive by email, so you can download and print everything immediately.',
  },
  {
    question: 'Does it work for all ages and levels?',
    answer:
      'Yes. The material runs from the very first chord to CAGED, blues and improvisation, and it’s written for kids, teens and adults alike — acoustic or electric.',
  },
  {
    question: 'Can I use this with all my guitar students?',
    answer:
      'Yes. Print as many copies as you need for every student in your private studio or guitar school — there are no extra fees per student.',
  },
  {
    question: 'How do I receive the material?',
    answer:
      'The PDFs are sent straight to the email address you use at checkout, right after your payment is confirmed — plus lifetime access to every future update at no extra cost. If anything doesn’t arrive, write to support@stockdigital.shop and we’ll sort it out.',
  },
  {
    question: 'What if I’m not completely satisfied?',
    answer:
      'You have 10 days. If your students aren’t more engaged, or you don’t save at least 5 hours of planning a week, email support@stockdigital.shop and we’ll refund every cent — no questions asked.',
  },
]

export const comparison: ComparisonRow[] = [
  {
    without: '5+ hours a week hunting for tabs and worksheets',
    with: 'Print and go · Every guitar sheet already prepared',
  },
  {
    without: 'Improvised lessons · No clear guitar progression',
    with: '300+ PDF pages · First chord to CAGED, step by step',
  },
  {
    without: 'Students bored by dusty guitar method books',
    with: 'Tabs, melodies and songs they actually want to play',
  },
  {
    without: 'Students quitting before their first full song',
    with: 'Playing recognizable songs within the first weeks',
  },
  {
    without: 'Sunday nights lost to formatting worksheets',
    with: 'Your whole guitar year mapped · Sundays back',
  },
]
